import BlockApi from '@/api/api';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { Release, ReleaseTarget } from '@/types/backend';
import PublicIcon from '@mui/icons-material/Public';
import { Button, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { useAuth } from 'react-oidc-context';

type RevisionListItemProps = {
  currentApplicationId?: string;
  currentRevision?: number;
  target: ReleaseTarget;
  appRevision?: Release;
  history?: Release[];
  onChange: () => void;
};

const RevisionListItem: FC<RevisionListItemProps> = (props) => {
  const { showProgress, closeProgress, showError } = useDialogs();
  const auth = useAuth();
  const { config } = useConfig();

  const handlePublish = async () => {
    if (!props.currentApplicationId || !props.currentRevision || !auth.user) return;

    showProgress(`Publishing revision ${props.currentRevision} to ${ReleaseTarget[props.target]}`);
    try {
      const response = await BlockApi(config.apiUrl, auth.user.access_token).release.create(
        props.currentApplicationId,
        props.currentRevision,
        props.target
      );

      if (response) {
        closeProgress();
        props.onChange();
      }
    } catch (err) {
      closeProgress();
      showError({
        title: 'Error while publishing',
        message: 'Something went wrong while publishing the current revision.',
        stacktrace: err
      });
    }
  };

  const handleRevert = async () => {
    if (
      !props.currentApplicationId ||
      !props.currentRevision ||
      !auth.user ||
      !props.history ||
      props.history.length < 1 ||
      !props.history[0].releaseId
    )
      return;
    showProgress(`Reverting to revision ${props.history[0]} for ${ReleaseTarget[props.target]}`);
    try {
      const response = await BlockApi(config.apiUrl, auth.user.access_token).release.revert(props.history[0].releaseId);
      if (response) {
        closeProgress();
        props.onChange();
      }
    } catch (err) {
      closeProgress();
      showError({
        title: 'Error while publishing',
        message: 'Something went wrong while publishing the current revision.',
        stacktrace: err
      });
    }
  };

  return (
    <Stack direction='row' gap={2} pl={3} pr={3}>
      <Typography variant='body1'>{ReleaseTarget[props.target]}</Typography>
      <Typography variant='body1' color='text.secondary' fontStyle={props.appRevision ? 'inherit' : 'italic'}>
        <code>{props.appRevision ? props.appRevision.revision : 'none'}</code>
      </Typography>
      <Stack
        direction='row'
        gap={1}
        sx={{
          flexGrow: 1,
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant='outlined'
          color='primary'
          size='small'
          disabled={
            !props.currentApplicationId ||
            !props.currentRevision ||
            props.currentRevision === props.appRevision?.revision
          }
          onClick={handlePublish}
          startIcon={<PublicIcon />}
        >
          Publish current
        </Button>
        {/* 
        // TODO: Uncomment this button when backend is updated with new data.
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!props.history || props.history.length < 1}
          onClick={handleRevert}
        >
          Revert to revision{' '}
          {props.history &&
            props.history.length > 0 &&
            props.history[0].revision}
        </Button> */}
      </Stack>
    </Stack>
  );
};

export default RevisionListItem;
